import type { PerUnit, ResolvedLine } from "./resolve-recipe";

/**
 * What a sale deducts, and what a correction gives back (FR-068, FR-069,
 * contracts/inventory-sync.md). Pure: resolved lines in, envelope payload and
 * deduction record out. Integers throughout — thousandths × units sold.
 *
 * THE RECORD IS WHY A VOID IS EXACT. A reversal is built from what the sale
 * actually took, per line and per unit, not by resolving the recipe again at
 * void time — re-resolving would credit whatever the recipe says NOW, and the
 * ledger would drift by every edit made in between. No record means the sale
 * deducted nothing, so there is nothing to give back: no phantom credit.
 */

export interface PayloadMovement {
  id: string;
  ingredientId: string;
  quantityDelta: number;
  unitCost: null;
  reason: null;
}

export interface PayloadUnmapped {
  menuItemId: string;
  itemName: string;
  quantity: number;
}

export interface MovementPayload {
  movements: PayloadMovement[];
  unmapped: PayloadUnmapped[];
  cause: "sale" | "sale_reversal";
  saleId: string;
  staffId: string;
  createdAt: string;
}

export interface DeductionRecordLine {
  lineId: string;
  /** Units sold on this line. */
  quantity: number;
  perUnit: PerUnit[];
}

export interface DeductionRecord {
  saleId: string;
  lines: DeductionRecordLine[];
}

export interface CorrectionContext {
  staffId: string;
  createdAt: string;
}

export const deductionRecordKey = (saleId: string) => `inventory:deduction:${saleId}`;

/** One movement per ingredient — UNIQUE(op_id, ingredient_id) — and none for a net zero. */
function toMovements(totals: ReadonlyMap<string, number>, newId: () => string): PayloadMovement[] {
  return [...totals]
    .filter(([, delta]) => delta !== 0)
    .map(([ingredientId, quantityDelta]) => ({
      id: newId(),
      ingredientId,
      quantityDelta,
      unitCost: null,
      reason: null,
    }));
}

function accumulate(totals: Map<string, number>, perUnit: readonly PerUnit[], times: number): void {
  for (const unit of perUnit) {
    totals.set(unit.ingredientId, (totals.get(unit.ingredientId) ?? 0) + unit.quantity * times);
  }
}

export function buildSaleDeduction(
  sale: { saleId: string; staffId: string; createdAt: string; lines: readonly ResolvedLine[] },
  newId: () => string,
): { payload: MovementPayload | null; record: DeductionRecord | null } {
  const totals = new Map<string, number>();
  const unmapped = new Map<string, PayloadUnmapped>();
  const recordLines: DeductionRecordLine[] = [];

  for (const line of sale.lines) {
    if (line.perUnit.length > 0) {
      accumulate(totals, line.perUnit, -line.quantity);
      recordLines.push({
        lineId: line.lineId,
        quantity: line.quantity,
        perUnit: line.perUnit.map((unit) => ({ ...unit })),
      });
    }

    if (!line.mapped) {
      const known = unmapped.get(line.menuItemId);
      unmapped.set(line.menuItemId, {
        menuItemId: line.menuItemId,
        itemName: line.itemName,
        quantity: (known?.quantity ?? 0) + line.quantity,
      });
    }
  }

  const movements = toMovements(totals, newId);

  return {
    payload:
      movements.length === 0 && unmapped.size === 0
        ? null
        : {
            movements,
            unmapped: [...unmapped.values()],
            cause: "sale",
            saleId: sale.saleId,
            staffId: sale.staffId,
            createdAt: sale.createdAt,
          },
    record: recordLines.length === 0 ? null : { saleId: sale.saleId, lines: recordLines },
  };
}

function reversal(
  record: DeductionRecord,
  totals: Map<string, number>,
  context: CorrectionContext,
  newId: () => string,
): MovementPayload | null {
  const movements = toMovements(totals, newId);
  if (movements.length === 0) return null;

  return {
    movements,
    unmapped: [],
    cause: "sale_reversal",
    saleId: record.saleId,
    staffId: context.staffId,
    createdAt: context.createdAt,
  };
}

/** Everything the sale took, given back. */
export function buildVoidReversal(
  record: DeductionRecord,
  context: CorrectionContext,
  newId: () => string,
): MovementPayload | null {
  const totals = new Map<string, number>();
  for (const line of record.lines) accumulate(totals, line.perUnit, line.quantity);
  return reversal(record, totals, context, newId);
}

/**
 * The refunded quantity of each refunded line, given back.
 *
 * Over-refunding is already impossible upstream — core caps cumulative refunds
 * per line (FR-025) — so each refund simply credits its own quantity.
 */
export function buildRefundReversal(
  record: DeductionRecord,
  refundLines: ReadonlyArray<{ saleLineId: string; quantity: number }>,
  context: CorrectionContext,
  newId: () => string,
): MovementPayload | null {
  const totals = new Map<string, number>();
  for (const refunded of refundLines) {
    const line = record.lines.find((candidate) => candidate.lineId === refunded.saleLineId);
    if (line) accumulate(totals, line.perUnit, refunded.quantity);
  }
  return reversal(record, totals, context, newId);
}
